import type { FundingApplicationSchemas } from "api-types";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import LocalizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
import { type IntlShape } from "react-intl";
import { MAX_DATE_NO_CSV, getEndDate, getRecievedPeriod, isEndDateCurrentMonth } from "shared-ui";

import { AuthCallbackPath } from "../../api-types/connector-callbacks";
import { CONNECT_APP_ROUTE_PATHS } from "../constants";

import { AIR_MESSAGES } from "./message-definitions";

dayjs.extend(LocalizedFormat);
dayjs.extend(utc);
dayjs.extend(duration);

export const AIR_IDENTIFIER_STORAGE_KEY = "funding-application-air-connect-identifier";

export const isEndDateEligibleForCSV = (endDateStr?: string | null) => {
  const endDate = getEndDate(endDateStr);
  return isEndDateCurrentMonth(endDateStr) && endDate.date() > MAX_DATE_NO_CSV;
};

export const getRecievedPeriodString = (
  period: FundingApplicationSchemas.DateTimeRangeSchema | null | undefined,
  intl: IntlShape
): string => {
  if (!period) {
    return intl.formatMessage(AIR_MESSAGES.periodMonths, { period: 0 });
  }

  const recievedPeriod = getRecievedPeriod(period);
  const recievedPeriodInMonths = Math.round(recievedPeriod.asMonths());

  if (recievedPeriodInMonths > 0) {
    return intl.formatMessage(AIR_MESSAGES.periodMonths, { period: recievedPeriodInMonths });
  }
  const recievedPeriodInWeeks = Math.round(recievedPeriod.asWeeks());
  return intl.formatMessage(AIR_MESSAGES.periodWeeks, { period: recievedPeriodInWeeks });
};

// Bank statement file types
export const getStatementTypeMessage = (
  intl: IntlShape,
  periods: FundingApplicationSchemas.DateTimeRangeSchema[]
): string => {
  // there should only ever be a max of one period with an end date eligible for CSV as its a single bank account
  const eligiblePeriod = periods.find((period) => isEndDateEligibleForCSV(period.end));
  const endDate = eligiblePeriod ? getEndDate(eligiblePeriod.end) : null;
  if (endDate && endDate.date() > MAX_DATE_NO_CSV) {
    return intl.formatMessage(AIR_MESSAGES.bankStatementPDForCSVText, {
      startOfMonth: endDate.startOf("month").format("LL"),
      currentDate: endDate.format("LL"),
    });
  }
  return intl.formatMessage(AIR_MESSAGES.bankStatementPDFText);
};

export const getAcceptedFileTypes = (
  periods: FundingApplicationSchemas.DateTimeRangeSchema[]
): {
  [key: string]: string[];
} => {
  if (periods.some((period) => isEndDateEligibleForCSV(period.end))) {
    return { "text/csv": [".csv"], "application/pdf": [".pdf"] };
  }
  return { "application/pdf": [".pdf"] };
};

export const getSupportedFileExtensions = (periods: FundingApplicationSchemas.DateTimeRangeSchema[]): string[] => {
  if (periods.some((period) => isEndDateEligibleForCSV(period.end))) {
    return ["pdf", "csv"];
  }
  return ["pdf"];
};

export const getPostAIRConnectionPath = (airOrigin?: string): AuthCallbackPath => {
  switch (airOrigin) {
    case CONNECT_APP_ROUTE_PATHS.ADDITIONAL_FUNDING:
      return AuthCallbackPath.ADDITIONAL_FUNDING_TASK_AIR_CONNECTION;
    case CONNECT_APP_ROUTE_PATHS.HOME:
      return AuthCallbackPath.CUSTOMER_HOME;
    default:
      return AuthCallbackPath.FUNDING_APPLICATION_AIRS_REVIEW;
  }
};
